<!-- Main Container -->
<div class="p-6">
  <!-- Header -->
  <div class="flex items-center justify-between mb-6">
    <div>
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Create Template from Event</h3>
      <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
        Select timeline items to convert into a reusable template
      </p>
    </div>
  </div>

  <form [formGroup]="form" (ngSubmit)="onSubmit()">
    <!-- Template Information -->
    <div class="flex items-center justify-between mb-5">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Template Information</h3>
    </div>

    <div class="-mx-2.5 mb-10 flex flex-wrap gap-y-5">
      <div class="w-full px-2.5">
        <app-text-input
          [control]="templateNameControl"
          [label]="'Template Name'"
          [placeholder]="'Enter template name'"
          [required]="true">
        </app-text-input>
      </div>

      <div class="w-full px-2.5">
        <app-select
          [control]="eventTypeControl"
          [label]="'Event Type'"
          [choices]="eventTypes"
          [required]="true">
        </app-select>
      </div>

      <div class="w-full px-2.5">
        <app-textarea
          [control]="templateDescriptionControl"
          [label]="'Description'"
          [placeholder]="'Describe this template (optional)'"
          [rows]="3">
        </app-textarea>
      </div>
    </div>

    <!-- Timeline Items Selection -->
    <div class="flex items-center justify-between mb-5">
      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">Select Timeline Items</h3>
      <div class="flex gap-2">
        <button
          type="button"
          (click)="selectAllItems()"
          class="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
        >
          Select All
        </button>
        <span class="text-gray-300 dark:text-gray-600">|</span>
        <button
          type="button"
          (click)="deselectAllItems()"
          class="text-sm text-gray-600 hover:text-gray-700 dark:text-gray-400"
        >
          Clear All
        </button>
      </div>
    </div>

    @if (timelineItems.length === 0) {
      <div class="p-8 text-center">
        <p class="text-gray-500 dark:text-gray-400">
          No timeline items found for this event.
        </p>
      </div>
    } @else {
      <table class="min-w-full mb-10">
        <thead class="border-t border-gray-100 dark:border-gray-800">
          <tr>
            <th class="border-r border-gray-200 px-4 py-3 dark:border-gray-800 w-12">
              <div class="flex items-center justify-center">
                <p class="text-xs font-medium text-gray-700 dark:text-gray-400">Select</p>
              </div>
            </th>
            <th class="border-r border-gray-200 px-4 py-3 dark:border-gray-800">
              <div class="flex items-center justify-between">
                <p class="text-xs font-medium text-gray-700 dark:text-gray-400">Timeline Item</p>
              </div>
            </th>
            <th class="border-r border-gray-200 px-4 py-3 dark:border-gray-800 w-32">
              <div class="flex items-center justify-center">
                <p class="text-xs font-medium text-gray-700 dark:text-gray-400">Date & Time</p>
              </div>
            </th>
            <th class="border-r border-gray-200 px-4 py-3 dark:border-gray-800 w-24">
              <div class="flex items-center justify-center">
                <p class="text-xs font-medium text-gray-700 dark:text-gray-400">Offset</p>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          @for (item of timelineItems; track item.id) {
            <tr 
              (click)="toggleTimelineItem(item.id)"
              class="h-12 odd:bg-gray-50 even:bg-white hover:bg-gray-100 dark:odd:bg-gray-800 dark:even:bg-gray-700 dark:hover:bg-gray-600 cursor-pointer"
            >
              <td class="border-r border-gray-100 px-4 py-3 dark:border-gray-800 text-center">
                <input
                  type="checkbox"
                  [checked]="isItemSelected(item.id)"
                  (click)="$event.stopPropagation()"
                  (change)="toggleTimelineItem(item.id)"
                  class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
              </td>
              <td class="border-r border-gray-100 px-4 py-3 dark:border-gray-800">
                <div>
                  <p class="text-sm font-medium text-gray-900 dark:text-white">
                    {{ item.name }}
                  </p>
                  @if (item.description) {
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1 line-clamp-2">
                      {{ item.description }}
                    </p>
                  }
                </div>
              </td>
              <td class="border-r border-gray-100 px-4 py-3 dark:border-gray-800 text-center">
                <div class="text-xs text-gray-500 dark:text-gray-400">
                  <div class="flex items-center justify-center mb-1">
                    <lucide-icon [name]="CalendarIcon" class="w-3 h-3 mr-1"></lucide-icon>
                    {{ item.datetime | date:'dd/MM/yyyy' }}
                  </div>
                  <div class="flex items-center justify-center">
                    <lucide-icon [name]="ClockIcon" class="w-3 h-3 mr-1"></lucide-icon>
                    {{ formatTime(item.datetime) }}
                  </div>
                </div>
              </td>
              <td class="border-r border-gray-100 px-4 py-3 dark:border-gray-800 text-center">
                <div class="text-xs text-blue-600 dark:text-blue-400">
                  @if (calculateDaysOffset(item.datetime) === 0) {
                    Event day
                  } @else if (calculateDaysOffset(item.datetime) > 0) {
                    +{{ calculateDaysOffset(item.datetime) }} day{{ calculateDaysOffset(item.datetime) === 1 ? '' : 's' }}
                  } @else {
                    {{ calculateDaysOffset(item.datetime) }} day{{ calculateDaysOffset(item.datetime) === -1 ? '' : 's' }}
                  }
                </div>
              </td>
            </tr>
          }
        </tbody>
      </table>
    }

    <!-- Selected Items Summary -->
    @if (hasSelectedItems) {
      <div class="mb-10 p-4 bg-blue-50 border border-blue-200 rounded-lg dark:bg-blue-900/20 dark:border-blue-800">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-blue-800 dark:text-blue-200">
              {{ selectedItemIds.size }} item{{ selectedItemIds.size === 1 ? '' : 's' }} selected
            </h3>
            <div class="mt-1 text-sm text-blue-700 dark:text-blue-300">
              <p>These items will be converted to template items with relative day offsets from the event start date.</p>
            </div>
          </div>
        </div>
      </div>
    }

    <!-- Footer -->
    <div class="w-full flex justify-end gap-3">
      <app-button
        [label]="'Cancel'"
        [style]="'secondary'"
        [disabled]="processing"
        (click)="onCancel()">
      </app-button>

      <app-button
        [label]="processing ? 'Creating...' : 'Create Template'"
        [style]="'primary'"
        [disabled]="!hasSelectedItems || form.invalid || processing"
        (click)="onSubmit()">
      </app-button>
    </div>
  </form>
</div>
